import { useMemo, type ReactElement, type ReactNode } from "react";
import { CalendarClock, Clock, ExternalLink } from "lucide-react";
import { color, space } from "@tokens/index";
import { Text } from "../../primitives";
import { summarizeRecipients } from "../workflowRecipients";
import type { WorkItem } from "../../../experience-shell/workflows";
import type { QueueTreeResponse } from "../../../experience-shell/queues/types";
import { formatDateTime, formatOverdueBy, prettifyTaskType } from "./formatters";
import { ActionButton, MetaChip } from "./shared";
import { PanelHeader } from "./PanelHeader";
import { TaskActions } from "./TaskActions";
import { TaskNotesSection } from "./TaskNotes";
import { TaskQueueAssignment } from "./TaskQueueAssignment";
import { ThreadSection } from "./ThreadSection";
import type { PanelNavigation, ThreadSectionProps } from "./types";

export interface TaskPanelProps extends PanelNavigation {
  readonly task: WorkItem;
  readonly now: Date;
  /** Every note on the workflow. The panel selects those attached to this task. */
  readonly notes: readonly WorkItem[];
  readonly onAddNote: (body: string, parentWorkItemId?: string) => void;
  readonly isAddingNote: boolean;
  readonly onPinNote: (noteId: string, pinned: boolean) => void;
  readonly isPinningId: string | null;
  readonly queueTree: QueueTreeResponse | undefined;
  readonly canApprove: boolean;
  readonly isCompleting: boolean;
  readonly isApproving: boolean;
  readonly isRejecting: boolean;
  readonly isSnoozing: boolean;
  readonly onComplete: (id: string) => void;
  readonly onApprove: ((id: string) => void) | undefined;
  readonly onReject: ((id: string, comment: string) => void) | undefined;
  readonly onSnooze: ((id: string, newDueDateIso: string) => void) | undefined;
  readonly onReassignQueue:
    | ((workItemId: string, currentQueueIds: readonly string[], newQueueId: string) => void)
    | undefined;
  readonly isReassigningQueue: boolean;
  readonly onNavigateToGalileo: (() => void) | undefined;
  /** Live-presence facepile for this task, supplied by the orchestrator. */
  readonly presenceSlot: ReactNode;
  /** Actions trail the title on `expanded` and up; else they wrap below it. */
  readonly actionsInline: boolean;
  readonly threadSection: ThreadSectionProps;
}

/**
 * The selected task. Header: title, one meta row (due, created, queue),
 * actions as one group (Complete, Snooze, Go). Below, notes and threads
 * share the height: notes take their content up to 45%, threads the rest.
 */
export function TaskPanel({
  task,
  now,
  notes,
  onAddNote,
  isAddingNote,
  onPinNote,
  isPinningId,
  queueTree,
  canApprove,
  isCompleting,
  isApproving,
  isRejecting,
  isSnoozing,
  onComplete,
  onApprove,
  onReject,
  onSnooze,
  onReassignQueue,
  isReassigningQueue,
  onNavigateToGalileo,
  presenceSlot,
  actionsInline,
  onBackToRail,
  threadSection,
}: TaskPanelProps): ReactElement {
  const assignedRecipients = useMemo(
    () => summarizeRecipients(task.visibleToQueueIds, threadSection.resolveQueueName),
    [task.visibleToQueueIds, threadSection.resolveQueueName],
  );

  const isComplete = task.completedAt !== null;
  const hasDue = task.dueDate !== null && task.dueDate.length > 0;
  const isOverdue = !isComplete && hasDue && new Date(task.dueDate ?? "").getTime() < now.getTime();
  const hasTaskType = task.taskType != null && task.taskType.length > 0;
  const titleText = hasTaskType && task.taskType != null ? prettifyTaskType(task.taskType) : task.body;
  const showBodyAside = hasTaskType && task.body.trim().length > 0;

  const actions = (
    <div
      data-testid="task-panel-actions"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: space[2],
        alignItems: "center",
        justifyContent: actionsInline ? "flex-end" : "flex-start",
      }}
    >
      <TaskActions
        task={task}
        now={now}
        canApprove={canApprove}
        isCompleting={isCompleting}
        isApproving={isApproving}
        isRejecting={isRejecting}
        isSnoozing={isSnoozing}
        onComplete={onComplete}
        onApprove={onApprove}
        onReject={onReject}
        onSnooze={onSnooze}
      />
      {onNavigateToGalileo !== undefined && (
        <ActionButton
          label="Go"
          ariaLabel="Open in Galileo"
          icon={<ExternalLink size={14} strokeWidth={2.25} aria-hidden="true" />}
          tone="ghost"
          onClick={onNavigateToGalileo}
        />
      )}
    </div>
  );

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
      <PanelHeader
        onBackToRail={onBackToRail}
        actionsInline={actionsInline}
        actions={actions}
        title={
          <>
            <Text as="h2" role="title" size="md" weight="semibold" style={{ minWidth: 0, overflowWrap: "anywhere", margin: 0 }}>
              {titleText}
            </Text>
            <div
              data-testid="task-panel-meta"
              style={{ display: "flex", alignItems: "center", gap: space[3], flexWrap: "wrap", minWidth: 0 }}
            >
              {hasDue && (
                <MetaChip
                  icon={<Clock size={12} strokeWidth={2} aria-hidden="true" />}
                  tone={isOverdue ? "danger" : "muted"}
                  testId="task-due-chip"
                >
                  Due {formatDateTime(task.dueDate ?? "")}
                  {isOverdue ? ` · overdue ${formatOverdueBy(task.dueDate ?? "", now)}` : ""}
                </MetaChip>
              )}
              <MetaChip icon={<CalendarClock size={12} strokeWidth={2} aria-hidden="true" />}>
                Created {formatDateTime(task.createdAt)}
              </MetaChip>
              <TaskQueueAssignment
                task={task}
                assignedRecipients={assignedRecipients}
                queueTree={queueTree}
                onReassign={onReassignQueue}
                isReassigning={isReassigningQueue}
              />
              {presenceSlot !== null && presenceSlot !== undefined && (
                <span style={{ display: "inline-flex", alignItems: "center", marginLeft: "auto" }}>{presenceSlot}</span>
              )}
            </div>
            {showBodyAside && (
              <div style={{ borderLeft: `2px solid ${color.border.subtle}`, paddingLeft: space[2], minWidth: 0 }}>
                <Text role="body" size="sm" tone="muted" style={{ wordBreak: "break-word" }}>
                  {task.body}
                </Text>
              </div>
            )}
          </>
        }
      />

      <TaskNotesSection
        taskId={task.id}
        notes={notes}
        onAddNote={onAddNote}
        isAddingNote={isAddingNote}
        onPinNote={onPinNote}
        isPinningId={isPinningId}
      />

      <ThreadSection {...threadSection} />
    </div>
  );
}
